import React, { ReactNode, useCallback, useEffect, useState } from "react";
import "./Recipe.css";
import { supabase } from "../../services/db";
import { Link, useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/Modal/Modal";
import { TrashIcon, Pencil2Icon } from "@radix-ui/react-icons";
import { useForm } from "react-hook-form";
import {
  ThemedPrimaryButton,
  ThemedSecondaryButton,
} from "../../components/Button/Button";

type Ingredient = {
  amount?: number;
  unit?: string;
  name?: string;
  note?: string;
  position?: number;
};

type Step = {
  description?: string;
  position?: number;
};

type RecipeInfo = {
  id: string;
  serves?: number;
  ingredients?: Ingredient[];
  name?: string;
  description?: string;
  author?: string;
  recipeUrl?: string;
  imgSrc?: string;
  imgInDatabase?: boolean;
  cookingTime: {
    totalTime: number;
    unit: string;
  };
  created_at: string;
  steps?: Step[];
};

const Recipe = () => {
  const { id, boardId } = useParams();
  const [recipeInfo, setRecipeInfo] = useState<RecipeInfo | null>(null);
  const [image, setImage] = useState<string>();
  const [servesValue, setServesValue] = useState<number>(0);
  const [editMode, setEditMode] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isLoading },
  } = useForm<RecipeInfo>();

  const handleSevesValueChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setServesValue(+event.target.value);
  };

  const onSubmit = async (data: RecipeInfo) => {
    const { error } = await supabase()
      .from("Recipe")
      .update({
        ...data,
      })
      .eq("id", data.id)
      .select();
    error && console.error(error);
    await getRecipeInfo(id);
    setEditMode(false);
  };

  const FormWrapper = ({
    children,
    mode,
  }: {
    children: ReactNode;
    mode: string;
  }) => {
    if (mode === "edit") {
      return (
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="defaultPageContainer">{children}</div>;
        </form>
      );
    }
    return <div className="defaultPageContainer">{children}</div>;
  };

  const navigate = useNavigate();

  const getImage = async (imgSrc: string, imgInDatabase: boolean) => {
    try {
      if (imgInDatabase) {
        const { data, error } = await supabase()
          .storage.from("recipe-images")
          .createSignedUrl(imgSrc, 3600);
        if (error) {
          throw new Error("Not found");
        }
        setImage(data?.signedUrl);
      } else {
        setImage(imgSrc);
      }
    } catch (e) {
      setImage(imgSrc);
    }
  };

  const getRecipeInfo = useCallback(
    async (recipeId?: string) => {
      if (!recipeId) {
        return;
      }
      const { data, error } = await supabase()
        .from("Recipe")
        .select()
        .eq("id", recipeId)
        .single();
      if (error) {
        console.error(error);
        return;
      }
      reset(data);
      data && setRecipeInfo(data);
      data.imgSrc && (await getImage(data.imgSrc, data.imgInDatabase));
      setServesValue(data.serves);
    },
    [reset]
  );

  useEffect(() => {
    getRecipeInfo(id);
  }, [id, getRecipeInfo]);

  const updateAmounts = (ingredient: Ingredient) => {
    if (!recipeInfo || !recipeInfo?.serves || !ingredient.amount) {
      return;
    }
    const scale = servesValue / recipeInfo?.serves;
    const updatedIngredient = {
      ...ingredient,
      amount: ingredient.amount * scale,
    };

    return updatedIngredient;
  };
  const handleRecipeDelete = async (handleClose: () => void) => {
    const { error } = await supabase()
      .from("SectionRecipe")
      .delete()
      .eq("RecipeId", id);

    if (error) {
      console.error(error);
      return;
    }

    const response = await supabase().from("Recipe").delete().eq("id", id);

    if (response.error) {
      return;
    }

    handleClose();
    navigate(-1);
  };

  const handleModeChange = () => {
    setEditMode(!editMode);
  };
  return (
    <FormWrapper mode={editMode ? "edit" : ""}>
      {boardId ? (
        <Link to={`/${boardId}`}>Back to board</Link>
      ) : (
        <div>
          <ThemedSecondaryButton onClick={() => navigate(-1)}>
            Back
          </ThemedSecondaryButton>
          <Link to="/">All boards</Link>
        </div>
      )}

      <div className="flexEnd">
        {!editMode ? (
          <ThemedPrimaryButton onClick={handleModeChange}>
            <Pencil2Icon /> Edit
          </ThemedPrimaryButton>
        ) : (
          <div>
            <ThemedPrimaryButton type="submit" disabled={isLoading}>
              Save
            </ThemedPrimaryButton>
            <ThemedSecondaryButton onClick={handleModeChange}>
              Cancel
            </ThemedSecondaryButton>
          </div>
        )}
        {!editMode && (
          <Modal
            title="Delete Recipe"
            description={"Are you sure you want to delete this recipe?"}
            buttonContent={
              <>
                <TrashIcon /> Delete
              </>
            }
            content={(onSubmitClose: () => void) => {
              return (
                <button onClick={() => handleRecipeDelete(onSubmitClose)}>
                  <TrashIcon /> Delete
                </button>
              );
            }}
          />
        )}
      </div>
      <div className="flexCenter">
        <div className="flexColoumn maxWidth800">
          <div className="flexCenter gap10">
            <div>
              {editMode ? (
                <div>
                  <label>Name</label>
                  <input {...register("name")} />
                </div>
              ) : (
                <h2>{recipeInfo?.name}</h2>
              )}

              {editMode ? (
                <div>
                  <label>Serves</label>
                  <input {...register("serves")} type="number" />
                </div>
              ) : (
                recipeInfo?.serves && (
                  <div>
                    Serves -
                    <input
                      value={servesValue}
                      type="number"
                      onChange={handleSevesValueChange}
                    />{" "}
                    people
                  </div>
                )
              )}
              {editMode ? (
                <div>
                  <label>Author</label>
                  <input {...register("author")} />
                </div>
              ) : (
                recipeInfo?.author && `Author: ${recipeInfo.author}`
              )}
              <div>
                {editMode ? (
                  <div>
                    <label>Recipe Url</label>
                    <input {...register("recipeUrl")} />
                  </div>
                ) : (
                  recipeInfo?.recipeUrl && (
                    <a href={recipeInfo.recipeUrl}>{recipeInfo.recipeUrl}</a>
                  )
                )}
              </div>
              <h3>Description</h3>
              {editMode ? (
                <div>
                  <label>Description</label>
                  <textarea {...register("description")} rows={5} cols={60} />
                </div>
              ) : (
                <div>{recipeInfo?.description}</div>
              )}
            </div>
            {image && (
              <img
                alt=""
                style={{ width: "250px", height: "250px" }}
                src={image}
                onLoad={() => {
                  URL.revokeObjectURL(image);
                }}
              />
            )}
          </div>
          <div>
            <h3>Ingredients</h3>
            {recipeInfo?.ingredients
              ?.filter((ingredient) => !!ingredient.name)
              .map((ingredient, i) => {
                if (editMode) {
                  return (
                    <fieldset key={i}>
                      <label>Ingredient {i + 1}</label>
                      <input {...register(`ingredients.${i}.name`)} />
                      <label>Amount</label>
                      <input
                        type="number"
                        step="any"
                        {...register(`ingredients.${i}.amount`)}
                      />
                      <label>Unit</label>
                      <input {...register(`ingredients.${i}.unit`)} />
                      <label>Note</label>
                      <input {...register(`ingredients.${i}.note`)} />
                    </fieldset>
                  );
                }
                const { amount, unit, note, name } =
                  updateAmounts(ingredient) || {};

                return (
                  <div key={(name || "") + i}>
                    {(amount || unit) && `${amount} ${unit} - `}
                    {name} {note}
                  </div>
                );
              })}
          </div>
          <div>
            <h3>Steps</h3>
            {recipeInfo?.steps
              ?.filter((step) => !!step.description)
              .map((step, i) =>
                editMode ? (
                  <textarea
                    key={i}
                    cols={60}
                    rows={6}
                    {...register(`steps.${i}.description`)}
                  />
                ) : (
                  <div key={step.description}>
                    {i + 1} - {step.description}
                  </div>
                )
              )}
          </div>
        </div>
      </div>
    </FormWrapper>
  );
};
export default Recipe;
